import type { Task } from "@app/core";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { clampFloatingPosition } from "../../lib/floating-position.js";
import { motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useDismiss } from "../../lib/useDismiss.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useSettingsStore } from "../../stores/index.js";
import { TimePicker } from "../task-form/TimePicker.js";
import { shouldShowAllDayLadderChoice } from "./ladder-schedule.js";

/** What the user picked on the ladder. "Keep time" is emitted as a plain time
 *  choice carrying the task's current hh:mm, so callers only see two kinds. */
export type LadderChoice =
  | { kind: "allDay" }
  | { kind: "time"; hour: number; minute: number };

interface Props {
  /** Release point of the drop — the ladder opens there. */
  x: number;
  y: number;
  /** Target strip day (local `YYYY-MM-DD`), shown in the header. */
  dayKey: string;
  task: Task;
  /** Hide the current-state rung when it would be a no-op (for example a
   *  same-day agenda reschedule). Cross-day drops keep it by default. */
  showCurrentChoice?: boolean;
  onPick: (choice: LadderChoice) => void;
  onClose: () => void;
}

interface Rung {
  key: string;
  label: string;
  detail: string | null;
  choice: LadderChoice;
}

/**
 * Time ladder — the follow-up to dropping a board chip on a load-strip day.
 * One tap picks the time of day (keep current / all-day / morning / afternoon /
 * evening), "Exact time…" swaps in the app's clock TimePicker. Dismissing
 * (outside click / Escape) cancels the whole drop — nothing is rescheduled.
 * Positioned + dismissed like {@link SnoozeMenu}.
 */
export function TimeLadder({ x, y, dayKey, task, showCurrentChoice = true, onPick, onClose }: Props) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const morningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const afternoonHour = useSettingsStore((s) => s.snoozeAfternoonHour);
  const eveningHour = useSettingsStore((s) => s.snoozeEveningHour);
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const [exact, setExact] = useState(false);

  const cur = task.due_at && !task.all_day ? new Date(task.due_at) : null;
  const [hm, setHm] = useState<{ h: number; m: number }>(() =>
    cur ? { h: cur.getHours(), m: cur.getMinutes() } : { h: morningHour, m: 0 },
  );

  useDismiss(onClose, { capture: true }, ref);

  const dayLabel = new Date(`${dayKey}T00:00:00`).toLocaleDateString(locale, {
    weekday: "short", day: "numeric", month: "short",
  });

  const rungs: Rung[] = [];
  if (cur && showCurrentChoice) {
    rungs.push({
      key: "keep",
      label: t("priorities.ladder.keep"),
      detail: tf.hm(cur.getHours(), cur.getMinutes()),
      choice: { kind: "time", hour: cur.getHours(), minute: cur.getMinutes() },
    });
  }
  // Timed items must always be able to convert to all-day. For an item that
  // is already all-day this is its current-state rung, so hide it alongside
  // "keep time" when a same-day caller suppresses no-op choices.
  if (shouldShowAllDayLadderChoice(task, showCurrentChoice)) {
    rungs.push({ key: "allDay", label: t("priorities.ladder.allDay"), detail: null, choice: { kind: "allDay" } });
  }
  rungs.push(
    { key: "morning", label: t("priorities.ladder.morning"), detail: tf.hm(morningHour, 0), choice: { kind: "time", hour: morningHour, minute: 0 } },
    { key: "afternoon", label: t("priorities.ladder.afternoon"), detail: tf.hm(afternoonHour, 0), choice: { kind: "time", hour: afternoonHour, minute: 0 } },
    { key: "evening", label: t("priorities.ladder.evening"), detail: tf.hm(eveningHour, 0), choice: { kind: "time", hour: eveningHour, minute: 0 } },
  );

  const position = clampFloatingPosition(
    x, y, window.innerWidth, window.innerHeight, 224, exact ? 408 : 268,
  );
  const style: React.CSSProperties = {
    position: "fixed",
    ...position,
    zIndex: 9999,
  };

  function pick(choice: LadderChoice) {
    onClose();
    onPick(choice);
  }

  return createPortal(
    <div ref={ref} style={style}>
      <motion.div
        initial={reduce ? false : { opacity: 0, scale: 0.96, y: -4 }}
        animate={reduce ? {} : { opacity: 1, scale: 1, y: 0 }}
        transition={springs.snappy}
        className="max-h-[calc(100vh-24px)] w-56 max-w-[calc(100vw-24px)] overflow-y-auto rounded-md border bg-card/95 text-card-foreground shadow-lg backdrop-blur-sm"
        onMouseDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        role="menu"
      >
        {!exact ? (
          <div className="flex flex-col py-1">
            <p className="px-3 pb-1 pt-1.5 text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">
              {t("priorities.ladder.title", { day: dayLabel })}
            </p>
            {rungs.map((r) => (
              <button
                key={r.key}
                type="button"
                role="menuitem"
                onClick={() => pick(r.choice)}
                className="flex items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
              >
                <span>{r.label}</span>
                {r.detail && <span className="shrink-0 text-xs text-muted-foreground">{r.detail}</span>}
              </button>
            ))}
            <div className="my-1 border-t" />
            <button
              type="button"
              role="menuitem"
              onClick={() => setExact(true)}
              className="px-3 py-1.5 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
            >
              {t("priorities.ladder.exact")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 p-2">
            <TimePicker hour={hm.h} minute={hm.m} onChange={(h, m) => setHm({ h, m })} />
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent/80" onClick={() => setExact(false)}>
                {t("common.back")}
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
                onClick={() => pick({ kind: "time", hour: hm.h, minute: hm.m })}
              >
                {t("priorities.ladder.commit")}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>,
    document.body,
  );
}
